import { Body, Box, Heading, Sheet, VStack } from '@hover/blueprint';
import { iconArrowLeft, iconArrowRight } from '@hover/icons';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useRef } from 'react';
import { generatePath, useHistory } from 'react-router';

import {
  useHomeownerAppContext,
  useScrollableLeftAndRight,
} from 'src/modules/homeownerApp/hooks';
import { IconButton } from 'src/shared/components';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { Route } from 'src/shared/route';
import { hideScrollbarsStyles, withHover } from 'src/shared/utils/styles';

import { MaterialSelectionCard } from './MaterialSelectionCard';

const MotionIconButton = motion(IconButton);

export const MaterialSelectionSheet = () => {
  const history = useHistory<{
    header?: string;
    subtitle?: string;
    projectId: number;
    assets: AiDisplayImage[];
  }>();
  const ref = useRef<HTMLDivElement>(null);
  const { setCurrentMenu } = useHomeownerAppContext();
  const { state } = history.location;

  const { canScrollLeft, canScrollRight, scroll } = useScrollableLeftAndRight({
    ref,
  });

  const handleMaterialClick = useCallback(
    (aiDisplayImage: AiDisplayImage) => {
      setCurrentMenu(null);
      history.push(
        generatePath(Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path, {
          projectId: state?.projectId,
          aiDisplayImageId: aiDisplayImage.id,
        }),
      );
    },
    [history, setCurrentMenu, state],
  );

  if (!state) return null;

  return (
    <Sheet.Contents maxH="100dvh" pb={{ base: 500, tablet: 800 }} pt={900}>
      <VStack
        alignItems="flex-start"
        data-testid="project-selection-sheet"
        overflow="hidden"
        w="100%"
      >
        <Box
          alignItems={{ base: 'center' }}
          flexDirection={{ base: 'column' }}
          justifyContent={{ base: 'center' }}
          pt={{ base: 500, tablet: 700 }}
          px={{ base: 500 }}
          width="100%"
        >
          <Heading
            marginY="0px"
            textStyle={{
              base: 'headingMedium',
              tablet: 'headingLarge',
              desktop: 'headingXl',
            }}
          >
            {state.header}
          </Heading>
          {state.subtitle && (
            <Body
              color="neutral.500"
              fontSize={{ base: 200, tablet: 400 }}
              m="0px !important"
            >
              {state.subtitle}
            </Body>
          )}
        </Box>
        <VStack gap={0} w="1">
          <Box
            gap={400}
            maxW="100%"
            mx="auto"
            overflowX="auto"
            pb={{ base: 500, tablet: 700 }}
            pt={{ base: 500, tablet: 500 }}
            px={{ base: 500 }}
            ref={ref}
            scrollBehavior="smooth"
            scrollSnapAlign="start"
            scrollSnapType="x mandatory"
            sx={hideScrollbarsStyles()}
          >
            {state.assets.map(asset => (
              <MaterialSelectionCard
                aiDisplayImage={asset}
                key={asset.id}
                onClick={handleMaterialClick}
              />
            ))}
          </Box>
          <Box px={{ base: 500 }} w={1}>
            <Box alignItems="center" flex={1} justifyContent="flex-end">
              <AnimatePresence>
                {(canScrollLeft || canScrollRight) && (
                  <>
                    <MotionIconButton
                      animate={{ opacity: 1 }}
                      disabled={!canScrollLeft}
                      display="none"
                      exit={{ opacity: 0 }}
                      icon={iconArrowLeft}
                      initial={{ opacity: 0 }}
                      label="scroll-left"
                      onClick={() => scroll('previous')}
                      sx={{ ...withHover({ display: 'flex' }) }}
                      variant="ghost"
                    />
                    <MotionIconButton
                      animate={{ opacity: 1 }}
                      disabled={!canScrollRight}
                      display="none"
                      exit={{ opacity: 0 }}
                      icon={iconArrowRight}
                      initial={{ opacity: 0 }}
                      label="scroll-right"
                      onClick={() => scroll('next')}
                      sx={{ ...withHover({ display: 'flex' }) }}
                      variant="ghost"
                    />
                  </>
                )}
              </AnimatePresence>
            </Box>
          </Box>
        </VStack>
      </VStack>
    </Sheet.Contents>
  );
};
